/**
 * Consumer Case Hooks
 *
 * Type-safe hooks for consumer case management using tRPC.
 */

import { useConsumerCase, useConsumerCases, useCreateConsumerCase } from '@/lib/trpc/hooks';

// Re-export types for backwards compatibility
export interface Case {
  id: string;
  title: string;
  type: string;
  caseNumber: string;
  status: string;
  priority?: string;
  metadata?: Record<string, unknown>;
  dueDate?: string | null;
  createdAt: Date;
  updatedAt: Date;
  documents?: CaseDocument[];
}

export interface CaseDocument {
  id: string;
  name: string;
  type: string;
  status: 'pending' | 'processing' | 'verified' | 'rejected';
  uploadedAt: string;
}

export interface TimelineEvent {
  id: string;
  type: string;
  description: string;
  createdAt: string;
  createdBy?: string;
}

export interface CreateCaseInput {
  caseType: string;
  metadata?: Record<string, unknown>;
}

// Export tRPC hooks with consumer-friendly names
export function useCases(
  page = 1,
  limit = 10,
  options?: { refetchInterval?: number; refetchOnWindowFocus?: boolean; enabled?: boolean },
) {
  const query = useConsumerCases({ page, limit }, options);
  // Transform response for backwards compatibility
  return {
    ...query,
    data: query.data
      ? {
          cases: query.data.data,
          total: query.data.total,
          page: query.data.page,
          limit: query.data.limit,
        }
      : undefined,
  };
}

export function useCase(id: string) {
  return useConsumerCase(id);
}

export function useCreateCase() {
  return useCreateConsumerCase();
}

// Document upload still uses REST (file uploads not supported by tRPC)
// Keep the REST implementation for file uploads
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';

async function uploadDocument(caseId: string, file: File, category: string) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);

  const response = await apiClient.post<CaseDocument>(`/consumer/cases/${caseId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export function useCaseDocumentUpload(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, category }: { file: File; category: string }) => uploadDocument(caseId, file, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumer', 'case', caseId] });
    },
  });
}

export default useCases;
